import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { storage } from '../../utils/platform';
import { API_BASE } from '../../config/api';

const STORAGE_KEY = 'revyo_current_restaurant_id';

// Quotas par plan — mirror du backend (routes/restaurants.js POST /).
// Fallback 1 si plan null/trial/inconnu.
//
// ⚠️ Depuis le chantier « palier Découverte » (30/07/2026), GET /api/restaurants
// renvoie `capabilities.max_restaurants` : on l'utilise en priorité, cette table
// locale n'est plus qu'un filet si l'API répond sans capabilities (rolling deploy
// d'une app mobile ancienne). `null` côté API = illimité (Infinity n'existe pas
// en JSON).
const QUOTA_BY_PLAN = { decouverte: 1, essentiel: 1, starter: 1, pro: 1, intensif: 3, premium: 3, groupe: Infinity };
function getQuotaForPlan(plan) {
  return Object.prototype.hasOwnProperty.call(QUOTA_BY_PLAN, plan) ? QUOTA_BY_PLAN[plan] : 1;
}

const CurrentRestaurantContext = createContext(null);

export function CurrentRestaurantProvider({ children }) {
  const [restaurants, setRestaurants] = useState([]);
  const [userPlan, setUserPlan] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentRestaurantId, setCurrentRestaurantIdState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; }
    catch (_) { return null; }
  });

  // UI sheets controlled state
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const getHeaders = useCallback(async () => {
    const token = await storage.get('token');
    return token
      ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      : { 'Content-Type': 'application/json' };
  }, []);

  const fetchRestaurants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_BASE}/api/restaurants`, { headers });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      const list = Array.isArray(data?.restaurants) ? data.restaurants : [];
      setRestaurants(list);
      setUserPlan(data?.user_plan || null);
      setCapabilities(data?.capabilities || null);
      // Validation du currentRestaurantId : doit appartenir à la liste retournée.
      // Si stocké mais resto disparu (suppression côté autre device, etc.) ou si
      // jamais stocké, on bascule sur le premier resto et on cleanup localStorage.
      setCurrentRestaurantIdState((prev) => {
        if (prev && list.some(r => r.id === prev)) return prev;
        const fallback = list[0]?.id || null;
        try {
          if (fallback) localStorage.setItem(STORAGE_KEY, fallback);
          else localStorage.removeItem(STORAGE_KEY);
        } catch (_) {}
        return fallback;
      });
    } catch (e) {
      console.error('[CurrentRestaurantContext] fetchRestaurants failed:', e.message);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  const setCurrentRestaurant = useCallback((id) => {
    if (!id) return;
    try { localStorage.setItem(STORAGE_KEY, id); } catch (_) {}
    setCurrentRestaurantIdState(id);
  }, []);

  const refetch = useCallback(() => fetchRestaurants(), [fetchRestaurants]);

  // POST /api/restaurants — création + auto-switch sur le nouveau resto.
  // Body : { name|nom, place_id|google_place_id, ville, type_cuisine }
  // Retour : { success: true, restaurant } ou { success: false, error, message, payload }.
  const addRestaurant = useCallback(async (payload) => {
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_BASE}/api/restaurants`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload || {}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return {
          success: false,
          error: data.error || `http_${res.status}`,
          message: data.message,
          payload: data,
        };
      }
      const newResto = data.restaurant;
      await fetchRestaurants();
      if (newResto?.id) setCurrentRestaurant(newResto.id);
      return { success: true, restaurant: newResto };
    } catch (e) {
      return { success: false, error: 'network_error', message: e.message };
    }
  }, [getHeaders, fetchRestaurants, setCurrentRestaurant]);

  const currentRestaurant = useMemo(
    () => restaurants.find(r => r.id === currentRestaurantId) || null,
    [restaurants, currentRestaurantId]
  );
  // max_restaurants vient de l'API quand elle l'expose ; null = illimité.
  const quota = useMemo(() => {
    if (capabilities && 'max_restaurants' in capabilities) {
      return capabilities.max_restaurants === null ? Infinity : capabilities.max_restaurants;
    }
    return getQuotaForPlan(userPlan);
  }, [capabilities, userPlan]);
  const quotaReached = useMemo(
    () => quota !== Infinity && restaurants.length >= quota,
    [quota, restaurants.length]
  );

  const openSwitcher = useCallback(() => setIsSwitcherOpen(true), []);
  const closeSwitcher = useCallback(() => setIsSwitcherOpen(false), []);
  const openAdd = useCallback(() => setIsAddOpen(true), []);
  const closeAdd = useCallback(() => setIsAddOpen(false), []);

  const value = useMemo(() => ({
    restaurants,
    currentRestaurant,
    currentRestaurantId,
    userPlan,
    capabilities,
    quota,
    quotaReached,
    loading,
    error,
    setCurrentRestaurant,
    refetch,
    addRestaurant,
    isSwitcherOpen, openSwitcher, closeSwitcher,
    isAddOpen, openAdd, closeAdd,
  }), [
    restaurants, currentRestaurant, currentRestaurantId, userPlan, capabilities, quota, quotaReached,
    loading, error, setCurrentRestaurant, refetch, addRestaurant,
    isSwitcherOpen, openSwitcher, closeSwitcher, isAddOpen, openAdd, closeAdd,
  ]);

  return (
    <CurrentRestaurantContext.Provider value={value}>
      {children}
    </CurrentRestaurantContext.Provider>
  );
}

export function useCurrentRestaurant() {
  const ctx = useContext(CurrentRestaurantContext);
  if (!ctx) {
    throw new Error('useCurrentRestaurant must be used within CurrentRestaurantProvider');
  }
  return ctx;
}
